import { Decimal } from 'decimal.js';
import { numberUtils, assetUtils } from '@zen/common-utils';

const { toValue } = numberUtils;
const { fromKalapas, toKalapas, getAssetFullId } = assetUtils;

/**
 * Convert order amounts to kalapas
 *
 * @param {{underlyingAmount: (number|string), orderTotal: (number|string), requestedPayout: (number|string), providedAmount: (number|string)}} [order={}]
 * @returns the amounts converted
 */
export function orderAmountsToKalapas(order = {}) {
  return {
    underlyingAmount: toKalapas(order.underlyingAmount || 0),
    orderTotal: toKalapas(order.orderTotal || 0),
    requestedPayout: toKalapas(order.requestedPayout || 0),
    providedAmount: toKalapas(order.providedAmount || 0),
  };
}

/**
 * Groups orders by "buy" and "sell" and adds price/quantity/total to each order
 *
 * @param {Object} params
 * @param {string} params.base - the base asset
 * @param {Array} params.orders - the orders array
 * @returns {{buy: Array, sell: Array}} the orders grouped and sorted by price, buys DESC, sells ASC
 */
export function ordersToBuysAndSells({ base, orders = [] } = {}) {
  return orders.reduce(
    (grouped, order) => {
      const buy = isBuy({ base, order });
      const orderWithPrice = Object.assign(
        {},
        order,
        buy ? getBuyAmounts(order) : getSellAmounts(order)
      );

      const insertIndex = buy
        ? grouped.buy.findIndex((item) =>
            new Decimal(orderWithPrice.price).greaterThanOrEqualTo(item.price)
          )
        : grouped.sell.findIndex((item) =>
            new Decimal(orderWithPrice.price).lessThanOrEqualTo(item.price)
          );

      if (insertIndex === -1) {
        grouped[buy ? 'buy' : 'sell'].push(orderWithPrice);
      } else {
        grouped[buy ? 'buy' : 'sell'].splice(insertIndex, 0, orderWithPrice);
      }

      return grouped;
    },
    { buy: [], sell: [] }
  );
}

/**
 * Get base/quote/price/quantity/total for a make by the side of the order
 *
 * @param {Object} order
 * @returns {{price: string, quantity: string, total:string, base: string, quote: string}}
 */
export function getMakeClientProps(order = {}) {
  const isBuy = order.makeSide !== 'sell'; // null is treated as a buy
  return isBuy
    ? { ...getBuyAmounts(order), base: order.pairAsset, quote: order.underlyingAsset }
    : { ...getSellAmounts(order), base: order.underlyingAsset, quote: order.pairAsset };
}

/**
 * Get base/quote/price/quantity/total for a take by the side of the order
 *
 * @param {Object} order
 * @returns {{price: string, quantity: string, total:string, base: string, quote: string}}
 */
export function getTakeClientProps(order = {}) {
  if (!['filled', 'partial_fill'].includes(order.status)) return {};

  const isBuy = order.takeSide !== 'sell'; // null is treated as a buy
  return isBuy
    ? { ...getTakeBuyAmounts(order), base: order.pairAsset, quote: order.underlyingAsset }
    : { ...getTakeSellAmounts(order), base: order.underlyingAsset, quote: order.pairAsset };
}

function getBuyAmounts(order) {
  return {
    price: toValue(new Decimal(order.underlyingAmount).dividedBy(order.orderTotal)),
    quantity: toValue(order.orderTotal),
    total: toValue(order.underlyingAmount),
  };
}
function getTakeBuyAmounts(order) {
  return {
    price: toValue(new Decimal(order.requestedPayout).dividedBy(order.providedAmount)),
    quantity: toValue(order.providedAmount),
    total: toValue(order.requestedPayout),
  };
}
function getSellAmounts(order) {
  return {
    price: toValue(new Decimal(order.orderTotal).dividedBy(order.underlyingAmount)),
    quantity: toValue(order.underlyingAmount),
    total: toValue(order.orderTotal),
  };
}
function getTakeSellAmounts(order) {
  return {
    price: toValue(new Decimal(order.providedAmount).dividedBy(order.requestedPayout)),
    quantity: toValue(order.requestedPayout),
    total: toValue(order.providedAmount),
  };
}

/**
 * Round quantity and total in favor of the maker
 *
 * @param {Object} params
 * @param {boolean} params.isBuyOrder - the type of the order.
 * @param {string} params.quantity - the quantity
 * @param {string} params.total - the total
 * @returns {{price: string, quantity: string, total: string}} an object containing the rounded amounts
 */
export function roundMakeAmounts({ isBuyOrder, quantity, total } = {}) {
  const quantityKalapas = toKalapas(quantity);
  const totalKalapas = toKalapas(total);
  const originalPrice = new Decimal(totalKalapas).dividedBy(quantityKalapas);

  if (
    new Decimal(quantityKalapas).decimalPlaces() > 0 ||
    new Decimal(totalKalapas).decimalPlaces() > 0
  ) {
    const roundedQuantity = Decimal.floor(quantityKalapas);
    const roundedTotal = isBuyOrder
      ? roundedQuantity.times(originalPrice).floor()
      : roundedQuantity.times(originalPrice).ceil();

    return {
      price: toValue(roundedTotal.dividedBy(roundedQuantity)),
      quantity: fromKalapas(roundedQuantity),
      total: fromKalapas(roundedTotal),
    };
  } else {
    return {
      price: toValue(originalPrice),
      quantity,
      total,
    };
  }
}

/**
 * Round quantity and total in favor of the maker in a take operation
 *
 * @param {Object} params
 * @param {boolean} params.isBuyOrder - the type of the original order.
 * @param {string} params.quantity - the quantity
 * @param {string} params.total - the total
 * @param {Object} params.order - the original order
 * @returns {{price: string, quantity: string, total: string}} an object containing the rounded amounts
 */
export function roundTakeAmounts({ isBuyOrder, quantity, total, order } = {}) {
  const quantityKalapas = toKalapas(quantity);
  const totalKalapas = toKalapas(total);
  const originalPrice = new Decimal(totalKalapas).dividedBy(quantityKalapas);
  let roundedQuantity, roundedTotal;
  if (isBuyOrder) {
    roundedQuantity = new Decimal(quantityKalapas).floor();
    roundedTotal = new Decimal(toKalapas(order.underlyingAmount))
      .times(roundedQuantity)
      .dividedBy(toKalapas(order.orderTotal))
      .floor();
  } else {
    roundedTotal = new Decimal(quantityKalapas).times(originalPrice).ceil();
    roundedQuantity = new Decimal(toKalapas(order.underlyingAmount))
      .times(roundedTotal)
      .dividedBy(toKalapas(order.orderTotal))
      .floor();
  }

  return {
    price: toValue(roundedTotal.dividedBy(roundedQuantity)),
    quantity: fromKalapas(roundedQuantity),
    total: fromKalapas(roundedTotal),
  };
}

/**
 * Convert base/quote/quantity/total to a Make order
 * converts all assets to full ID
 *
 * @param {Object} params
 * @param {Object} params.isBuy - the type of the make order
 * @param {string} params.base - the base asset
 * @param {string} params.quote - the quote asset
 * @param {string} params.quantity - the quantity
 * @param {string} params.total - the total
 * @returns {Object} a make order
 */
export function getMakeOrder({ isBuy, base, quote, quantity, total } = {}) {
  return isBuy
    ? {
        underlyingAsset: getAssetFullId(quote),
        underlyingAmount: total,
        pairAsset: getAssetFullId(base),
        orderTotal: quantity,
      }
    : {
        underlyingAsset: getAssetFullId(base),
        underlyingAmount: quantity,
        pairAsset: getAssetFullId(quote),
        orderTotal: total,
      };
}

/**
 * Convert base/quote/quantity/total to a Take order
 *
 * @param {Object} params
 * @param {Object} params.order - the original order object
 * @param {string} params.base - the base asset
 * @param {string} params.quantity - the quantity
 * @param {string} params.total - the total
 * @returns {Object} the order modified with the take parameters
 */
export function getTakeOrder({ order, base, quantity, total } = {}) {
  const buy = isBuy({ base, order });

  const providedAmount = buy ? quantity : total;
  const requestedPayout = buy ? total : quantity;
  return Object.assign({}, order, { requestedPayout, providedAmount });
}

/**
 * Calculate the price of a quantity/total
 *
 * @param {Object} params
 * @param {string} params.quantity
 * @param {string} params.total
 * @returns {string} the calculated price
 */
export function calculatePrice({ quantity, total } = {}) {
  return toValue(new Decimal(total).dividedBy(quantity));
}
/**
 * Checks if order is a buy order
 *
 * @param {Object} params
 * @param {string} params.base - the base asset
 * @param {Object} params.order - the order
 * @returns {boolean} true if order is a buy order, otherwise false
 */
export function isBuy({ base, order }) {
  return order.pairAsset === getAssetFullId(base);
}
